import {
  Text,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  Stack,
  ModalFooter,
  useColorMode,
  useColorStyle,
  Skeleton,
} from '@tonic-one/react';
import { FormLabel } from '@Components/Common/FormComponent';
import { useTranslation } from 'react-i18next';
import { CorrelatedRuleShallow } from '../types';
import { RiskAIApi } from '@Apis';
import { useOrgStore } from '@Models/useOrgModel';
import RuleContent from './RuleContent';
import { useQuery } from '@tanstack/react-query';

const PreDefinedRuleModal = (props: {
  rule: CorrelatedRuleShallow;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { rule: ruleShallow, isOpen, onClose } = props;
  const { orgId } = useOrgStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  const { data: ruleContent, isLoading } = useQuery({
    queryKey: ['correlatedRule', orgId, ruleShallow.raiRuleId],
    queryFn: () => RiskAIApi.getRule(orgId, ruleShallow.raiRuleId),
    select: ({ data }) => data.raiRuleContent,
    meta: {
      errorNotification: {
        title: t('common.apiException.internalError'),
      },
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      closeOnEsc
      closeOnOutsideClick
      isClosable
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {t('globalSetting.correlatedRules.preDefinedModal.title')}
        </ModalHeader>
        <ModalBody>
          <Stack gap="4x">
            <Text size="lg">
              {t(
                'globalSetting.correlatedRules.preDefinedModal.basicProperties'
              )}
            </Text>
            <Flex alignItems="center">
              <FormLabel
                label={t('globalSetting.correlatedRules.preDefinedModal.name')}
              />
              <Text>{ruleShallow.raiRuleName}</Text>
            </Flex>
            <Flex>
              <FormLabel
                label={t(
                  'globalSetting.correlatedRules.preDefinedModal.description'
                )}
              />
              <Text height="max-content" my="auto">
                {ruleShallow.raiRuleDesc}
              </Text>
            </Flex>
            <Stack gap="1x" mt="4x">
              <Text size="lg">
                {t(
                  'globalSetting.correlatedRules.preDefinedModal.ruleDefinition'
                )}
              </Text>
              <Text color={colorStyle.color.secondary}>
                {t(
                  'globalSetting.correlatedRules.preDefinedModal.ruleDefinitionDesc'
                )}
              </Text>
            </Stack>
            <Flex>
              <FormLabel
                label={t('globalSetting.correlatedRules.preDefinedModal.rule')}
              />
              {isLoading && (
                <Skeleton width="75%" height="48px" animation="pulse" />
              )}
              {!isLoading && ruleContent && (
                <RuleContent
                  content={ruleContent}
                  gap="2x"
                  justifyContent="center"
                  minHeight="8x"
                />
              )}
            </Flex>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>{t('common.button.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PreDefinedRuleModal;
